import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Layout, Cpu, Database, Terminal } from 'lucide-react';
import { SkeletonBlock } from '../components/common/SkeletonBlock';
import { GlassPanel } from '../components/common/GlassPanel';

const getSkillLabel = (level) => {
  if (level >= 80) return "MASTERED";
  if (level >= 60) return "PROFICIENT";
  return "LEARNING";
};

const SKILL_GROUPS = [
  {
    title: "FRONTEND",
    icon: <Layout />,
    items: [
      { name: "React", level: 75 },
      { name: "Tailwind", level: 70 },
      { name: "Framer Motion", level: 50 },
      { name: "JavaScript", level: 90 },
      { name: "HTML/CSS", level: 95 }
    ]
  },
  {
    title: "BACKEND",
    icon: <Cpu />,
    items: [
      { name: "Node.js", level: 60 },
      { name: "Express", level: 40 },
      { name: "MongoDB", level: 45 },
      { name: "REST APIs", level: 30 },
      { name: "PostgreSQL", level: 25 }
    ]
  },
  {
    title: "DEVOPS",
    icon: <Database />,
    items: [
      { name: "Vercel", level: 90 },
      { name: "GitHub", level: 85 },
      { name: "Netlify", level: 80 },
      { name: "Basic CI/CD", level: 75 },
      { name: "Deployment", level: 90 }
    ]
  },
  {
    title: "TOOLS",
    icon: <Terminal />,
    items: [
      { name: "Git", level: 75 },
      { name: "VS Code", level: 95 },
      { name: "Figma", level: 65 }
    ]
  }
];

const SkillsImpl = ({ accent, mood, isResolving }) => {
  return (
  <div className="min-h-screen pt-32 pb-48 px-4 sm:px-4 sm:px-8 lg:px-24">
    <h2 className="text-5xl font-black italic text-white mb-16 uppercase tracking-tighter">TECHNICAL SKILLS</h2>
    <AnimatePresence mode="wait">
      {isResolving ? (
        <div key="skeleton" className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {[1,2,3,4].map(i => <SkeletonBlock key={i} className="h-[400px] w-full rounded-[2rem]" />)}
        </div>
      ) : (
        <motion.div key="content" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 relative z-10">
          {SKILL_GROUPS.map((group, idx) => (
            <GlassPanel key={idx} mood={mood} className="p-6 sm:p-10 rounded-[2rem] group transition-colors hover:border-white/10">
              <div className="mb-8 p-4 rounded-xl bg-white/5 inline-block group-hover:rotate-12 transition-transform" style={{ color: accent.color }}>{group.icon}</div>
              <h3 className="text-xl font-black italic text-white mb-8 uppercase tracking-widest">{group.title}</h3>
              <motion.div
                className="flex flex-col gap-6"
                initial="hidden"
                animate="show"
                variants={{
                  hidden: {},
                  show: {
                    transition: {
                      staggerChildren: 0.12
                    }
                  }
                }}
              >
                {group.items.map((skill, sIdx) => (
                  <motion.div
                    key={sIdx}
                    className="flex flex-col gap-2"
                    variants={{
                      hidden: { opacity: 0, y: 10 },
                      show: {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.4, ease: "easeOut" }
                      }
                    }}
                  >

                    <div className="flex justify-between text-[10px] font-black uppercase text-white/30">
                      <span>{skill.name}</span>
                      <span style={{ color: accent.color }}>
                        {getSkillLabel(skill.level)}
                      </span>
                    </div>

                    <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">

                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${skill.level}%` }}
                        transition={{ duration: 1.2, ease: "circOut" }}
                        className="h-full"
                        style={{ backgroundColor: accent.color }}
                      />

                    </div>

                  </motion.div>
                ))}
              </motion.div>
            </GlassPanel>
          ))}
        </motion.div>
      )}
    </AnimatePresence>
  </div>
  );
};

export const Skills = React.memo(SkillsImpl);
